import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface SystemOverview {
  status: 'OPERATIONAL' | 'WARNING' | 'CRITICAL';
  lastTrade: string | null;
  lastTradeMinutes: number | null;
  marketStatus: 'OPEN' | 'CLOSED';
  etTime: string;
  activeUsers: number;
  timestamp: string;
}

interface Service {
  status: 'healthy' | 'warning' | 'error' | 'down';
  message: string;
  latency?: string;
  hitRate?: string;
  version?: string;
  lastRun?: string;
  lastRunEver?: string;
  lastRunEverEt?: string;
  nextRunAt?: string;
  nextRunAtEt?: string;
  runsToday?: number;
  tradingUniverseTodayRuns?: number;
  lastTradingUniverseRunToday?: string;
  lastTradingUniverseRunTodayEt?: string;
  lastRecorded?: string;
  lastRecordedEt?: string;
  recordsLast7Days?: number;
  recentTrades?: number;
  lastEtTickerTrigger?: string;
  lastEtTickerTriggerEt?: string;
  lastMarketScreening?: string;
  lastMarketScreeningEt?: string;
  etTickerTriggersLastHour?: number;
  marketScreeningLastHour?: number;
  ordersLast24h?: number;
  ordersLast3Days?: number;
  isMarketHours?: boolean;
  lastOrder?: {
    createdAt: string;
    createdAtEt: string;
    status: string;
    symbol: string;
    side: string;
  };
}

interface Activity {
  id: string;
  type: string;
  icon: string;
  message: string;
  timestamp: string;
  userId?: string;
}

interface Alert {
  level: 'critical' | 'warning' | 'info';
  title: string;
  message: string;
  action: string;
  timestamp: string;
}

interface RejectedOrderDetail {
  symbol: string;
  side: string;
  reason: string | null;
  reasonCode: string | null;
  createdAt: string;
}

interface TradingStats {
  totalOrders: number;
  filledOrders: number;
  rejectedOrders: number;
  rejectedDetails: RejectedOrderDetail[];
  trades: number;
  volume: number;
  symbols: number;
}

interface ConfigFile {
  name: string;
  status: string;
  version?: string;
  message?: string;
}

interface JobExecution {
  runAt: string | null;
  finishedAt?: string | null;
  failedAt?: string | null;
  success: boolean;
  error?: string | null;
}

interface Job {
  name: string;
  executions: string;
  successRate: string;
  status: 'healthy' | 'warning' | 'error';
  lastRun: string | null;
  lastFinishedAt?: string | null;
  lastFailedAt?: string | null;
  failCount?: number;
  failReason?: string | null;
  nextRun?: string | null;
  recentExecutions?: JobExecution[];
}

interface TradingDaySummary {
  date: string;
  marketScreeningRuns: number;
  tradingUniverseRuns: number;
  ordersPlaced: number;
}

interface UserStats {
  id: string;
  email: string;
  name: string;
  status: string;
  positions: number;
  pnl: string;
  lastTrade: string | null;
  lastTradeMinutes: number | null;
  ordersToday: number;
}

const SystemHealth: React.FC = () => {
  const { user } = useAuth();
  const [overview, setOverview] = useState<SystemOverview | null>(null);
  const [services, setServices] = useState<Record<string, Service>>({});
  const [activities, setActivities] = useState<Activity[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [stats, setStats] = useState<TradingStats | null>(null);
  const [configs, setConfigs] = useState<ConfigFile[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [currentTradingDay, setCurrentTradingDay] = useState<TradingDaySummary | null>(null);
  const [previousTradingDay, setPreviousTradingDay] = useState<TradingDaySummary | null>(null);
  const [users, setUsers] = useState<UserStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchData = async () => {
    try {
      const [
        overviewRes,
        servicesRes,
        activitiesRes,
        alertsRes,
        statsRes,
        configsRes,
        jobsRes,
        usersRes
      ] = await Promise.all([
        api.get('/system-health/overview'),
        api.get('/system-health/services'),
        api.get('/system-health/activity?limit=20'),
        api.get('/system-health/alerts'),
        api.get('/system-health/stats'),
        api.get('/system-health/config'),
        api.get('/system-health/scheduler'),
        api.get('/system-health/users')
      ]);

      const statsPayload = statsRes.data?.today || {};
      const normalizedStats: TradingStats = {
        totalOrders: statsPayload.orders ?? 0,
        filledOrders: statsPayload.filled ?? 0,
        rejectedOrders: statsPayload.rejected ?? 0,
        rejectedDetails: statsPayload.rejectedDetails ?? [],
        trades: statsPayload.trades ?? 0,
        volume: Number(statsPayload.volume ?? 0),
        symbols: statsPayload.symbols ?? 0
      };

      const configPayload: Record<string, any> = configsRes.data || {};
      const normalizedConfigs: ConfigFile[] = Object.keys(configPayload).map((key) => {
        const value = configPayload[key] || {};
        return {
          name: key.replace(/([A-Z])/g, ' $1').replace(/\b\w/g, (char) => char.toUpperCase()).trim(),
          status: value.status || 'unknown',
          version: value.version,
          message: value.message || (value.status === 'loaded' ? 'Config file present' : value.path || 'Missing')
        };
      });

      const normalizedAlerts: Alert[] = Array.isArray(alertsRes.data)
        ? alertsRes.data.map((alert) => ({
            level: alert.level || alert.severity || 'info',
            title: alert.title || alert.message || 'System Alert',
            message: alert.message || alert.details || 'No additional details',
            action: alert.action || 'Investigate',
            timestamp: alert.timestamp || new Date().toISOString()
          }))
        : [];

      const normalizedActivities: Activity[] = Array.isArray(activitiesRes.data)
        ? activitiesRes.data.map((activity) => ({
            id: activity.id || `${activity.type || 'activity'}-${activity.timestamp || Date.now()}`,
            type: activity.type || activity.eventType || 'SYSTEM',
            icon: activity.icon || (activity.severity === 'error' ? '❗' : activity.severity === 'success' ? '✅' : '📝'),
            message: activity.message || activity.details || activity.type || 'Activity recorded',
            timestamp: activity.timestamp || new Date().toISOString(),
            userId: activity.userId
          }))
        : [];

      setOverview(overviewRes.data);
      setServices(servicesRes.data || {});
      setActivities(normalizedActivities);
      setAlerts(normalizedAlerts);
      const normalizedJobs: Job[] = (jobsRes.data?.jobs || []).map((job: any) => ({
        name: job.name,
        executions: String(job.runsLastHour ?? job.executions ?? 0),
        successRate: typeof job.successRate === 'number' ? `${job.successRate}%` : (job.successRate || (job.runsLastHour && job.runsLastHour > 0 ? '100%' : '0%')),
        status: job.status === 'running' ? 'healthy' : job.status === 'idle' ? 'warning' : job.status || 'warning',
        lastRun: job.lastRun || null,
        lastFinishedAt: job.lastFinishedAt || null,
        lastFailedAt: job.lastFailedAt || null,
        failCount: job.failCount || 0,
        failReason: job.failReason || null,
        nextRun: job.nextRun || null,
        recentExecutions: job.recentExecutions || []
      }));

      setStats(normalizedStats);
      setConfigs(normalizedConfigs);
      setJobs(normalizedJobs);
      setCurrentTradingDay(jobsRes.data?.currentTradingDay || null);
      setPreviousTradingDay(jobsRes.data?.previousTradingDay || null);
      setUsers(usersRes.data?.users || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching system health data:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [user]);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchData();
    }, 10000); // Refresh every 10 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, user]);

  const handlePauseAll = async () => {
    if (!window.confirm('Are you sure you want to pause ALL trading? This will stop all automated trading immediately.')) {
      return;
    }

    try {
      await api.post('/system-health/actions', { action: 'pause_all_automation' });
      alert('All trading has been paused');
      fetchData();
    } catch (error) {
      console.error('Error pausing trading:', error);
      alert('Failed to pause trading');
    }
  };

  const handleResumeAll = async () => {
    if (!window.confirm('Are you sure you want to resume ALL trading?')) {
      return;
    }

    try {
      await api.post('/system-health/actions', { action: 'resume_all_automation' });
      alert('All trading has been resumed');
      fetchData();
    } catch (error) {
      console.error('Error resuming trading:', error);
      alert('Failed to resume trading');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'OPERATIONAL':
      case 'healthy':
      case 'loaded':
        return '🟢';
      case 'WARNING':
      case 'warning':
        return '🟡';
      case 'CRITICAL':
      case 'error':
      case 'missing':
        return '🔴';
      default:
        return '⚪';
    }
  };

  const formatTime = (timestamp: string | null) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return `${Math.floor(diffMins / 1440)}d ago`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-neutral-950">
        <div className="text-white">Loading system health data...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-950 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => window.history.back()}
              className="px-3 py-2 bg-neutral-800 hover:bg-neutral-700 text-neutral-300 hover:text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back
            </button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white">System Health Monitor</h1>
              <p className="text-neutral-400 text-sm mt-1">Real-time trading system diagnostics</p>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                autoRefresh 
                  ? 'bg-green-600 hover:bg-green-700 text-white' 
                  : 'bg-neutral-800 hover:bg-neutral-700 text-neutral-300'
              }`}
            >
              {autoRefresh ? '🔄 Auto-Refresh ON' : '⏸️ Auto-Refresh OFF'}
            </button>
            <button
              onClick={fetchData}
              className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              🔄 Refresh Now
            </button>
          </div>
        </div>

        {/* Phase 1: Status Overview Banner */}
        {overview && (
          <div className={`rounded-lg p-6 ${
            overview.status === 'OPERATIONAL' ? 'bg-green-900/30 border-2 border-green-600' :
            overview.status === 'WARNING' ? 'bg-yellow-900/30 border-2 border-yellow-600' :
            'bg-red-900/30 border-2 border-red-600'
          }`}>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{getStatusIcon(overview.status)}</span>
                  <div>
                    <h2 className="text-xl sm:text-2xl font-bold text-white">
                      SYSTEM {overview.status}
                    </h2>
                    <p className="text-neutral-300 text-sm">
                      Last Trade: {overview.lastTradeMinutes !== null ? `${overview.lastTradeMinutes} minutes ago` : 'No trades today'}
                    </p>
                  </div>
                </div>
              </div>
              <div className="text-left sm:text-right space-y-1">
                <div className="text-sm text-neutral-300">
                  Market Status: <span className={`font-bold ${overview.marketStatus === 'OPEN' ? 'text-green-400' : 'text-red-400'}`}>
                    {overview.marketStatus}
                  </span>
                </div>
                <div className="text-sm text-neutral-300">
                  ET Time: <span className="text-white">{new Date(overview.etTime).toLocaleTimeString('en-US', { hour12: true })}</span>
                </div>
                <div className="text-sm text-neutral-300">
                  Active Users: <span className="text-white font-bold">{overview.activeUsers}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Phase 1: Critical Alerts Panel */}
        {alerts.length > 0 && (
          <div className="bg-neutral-900 rounded-lg border border-neutral-800 p-6">
            <h3 className="text-xl font-bold text-white mb-4">⚠️ Alerts & Warnings</h3>
            <div className="space-y-3">
              {alerts.map((alert, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-lg border-l-4 ${
                    alert.level === 'critical' ? 'bg-red-900/20 border-red-600' :
                    alert.level === 'warning' ? 'bg-yellow-900/20 border-yellow-600' :
                    'bg-blue-900/20 border-blue-600'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-red-400 mb-1">
                        {alert.level === 'critical' ? '🔴 CRITICAL' : 
                         alert.level === 'warning' ? '🟡 WARNING' : '🔵 INFO'}
                      </div>
                      <div className="font-semibold text-white mb-1">{alert.title}</div>
                      <div className="text-sm text-neutral-300 mb-2">{alert.message}</div>
                      <div className="text-xs text-neutral-400">Action: {alert.action}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Phase 1: Critical Services Health Cards */}
        <div>
          <h3 className="text-xl font-bold text-white mb-4">Service Health</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(services)
              .filter(([key]) => !['automation', 'jobExecution', 'rejectionAnalytics', 'tradingActivity'].includes(key))
              .map(([key, service]) => (
              <div key={key} className="bg-neutral-900 border border-neutral-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-2xl">{getStatusIcon(service.status)}</span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    service.status === 'healthy' ? 'bg-green-600 text-white' :
                    service.status === 'warning' ? 'bg-yellow-600 text-white' :
                    'bg-red-600 text-white'
                  }`}>
                    {service.status.toUpperCase()}
                  </span>
                </div>
                <h4 className="text-white font-semibold capitalize mb-1">
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </h4>
                <p className="text-sm text-neutral-300 mb-2">{service.message}</p>
                <div className="text-xs text-neutral-400 space-y-1">
                  {service.latency && <div>Latency: {service.latency}</div>}
                  {service.hitRate && <div>Hit Rate: {service.hitRate}</div>}
                  {service.version && <div>Version: {service.version}</div>}
                  {service.lastRun && <div>Last Run: {formatTime(service.lastRun)}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Trading Operations Status */}
        <div>
          <h3 className="text-xl font-bold text-white mb-4">Trading Operations</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            
            {/* Market Screening */}
            {services['marketScreening'] && (
              <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-2xl">{getStatusIcon(services['marketScreening'].status)}</span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    services['marketScreening'].status === 'healthy' ? 'bg-green-600 text-white' :
                    services['marketScreening'].status === 'warning' ? 'bg-yellow-600 text-white' :
                    'bg-red-600 text-white'
                  }`}>
                    {services['marketScreening'].status.toUpperCase()}
                  </span>
                </div>
                <h4 className="text-white font-semibold mb-1">Market Screening</h4>
                <p className="text-sm text-neutral-300 mb-2">{services['marketScreening'].message}</p>
                <div className="text-xs space-y-1">
                  {services['marketScreening'].lastRunEverEt && (
                    <div>
                      <div className="text-white font-medium">{services['marketScreening'].lastRunEverEt}</div>
                      <div className="text-neutral-500">({formatTime(services['marketScreening'].lastRunEver || '')})</div>
                    </div>
                  )}
                  {services['marketScreening'].nextRunAtEt && (
                    <div className="mt-2">
                      <div className="text-neutral-400">Next Run:</div>
                      <div className="text-white font-medium">{services['marketScreening'].nextRunAtEt}</div>
                      <div className="text-neutral-500">({formatTime(services['marketScreening'].nextRunAt || '')})</div>
                    </div>
                  )}
                  {services['marketScreening'].runsToday !== undefined && (
                    <div className="text-neutral-400 mt-2">Runs Today: <span className="text-white">{services['marketScreening'].runsToday}</span></div>
                  )}
                </div>
              </div>
            )}

            {/* Automated Trading */}
            {services['automatedTrading'] && (
              <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-2xl">{getStatusIcon(services['automatedTrading'].status)}</span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    services['automatedTrading'].status === 'healthy' ? 'bg-green-600 text-white' :
                    services['automatedTrading'].status === 'warning' ? 'bg-yellow-600 text-white' :
                    'bg-red-600 text-white'
                  }`}>
                    {services['automatedTrading'].status.toUpperCase()}
                  </span>
                </div>
                <h4 className="text-white font-semibold mb-1">Automated Trading</h4>
                <p className="text-sm text-neutral-300 mb-2">{services['automatedTrading'].message}</p>
                <div className="text-xs space-y-1">
                  {services['automatedTrading'].lastRunEverEt && (
                    <div>
                      <div className="text-white font-medium">{services['automatedTrading'].lastRunEverEt}</div>
                      <div className="text-neutral-500">({formatTime(services['automatedTrading'].lastRunEver || '')})</div>
                    </div>
                  )}
                  {services['automatedTrading'].tradingUniverseTodayRuns !== undefined && (
                    <div className="text-neutral-400 mt-2">Universe Runs Today: <span className="text-white">{services['automatedTrading'].tradingUniverseTodayRuns}</span></div>
                  )}
                  {services['automatedTrading'].lastTradingUniverseRunTodayEt && (
                    <div className="mt-2">
                      <div className="text-neutral-400">Last Universe Run:</div>
                      <div className="text-white font-medium">{services['automatedTrading'].lastTradingUniverseRunTodayEt}</div>
                      <div className="text-neutral-500">({formatTime(services['automatedTrading'].lastTradingUniverseRunToday || '')})</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Trading Activity */}
            {services['tradingActivity'] && (
              <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-2xl">{getStatusIcon(services['tradingActivity'].status)}</span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    services['tradingActivity'].status === 'healthy' ? 'bg-green-600 text-white' :
                    services['tradingActivity'].status === 'warning' ? 'bg-yellow-600 text-white' :
                    'bg-red-600 text-white'
                  }`}>
                    {services['tradingActivity'].status.toUpperCase()}
                  </span>
                </div>
                <h4 className="text-white font-semibold mb-1">Trading Activity</h4>
                <p className="text-sm text-neutral-300 mb-2">{services['tradingActivity'].message}</p>
                <div className="text-xs space-y-1">
                  {services['tradingActivity'].lastOrder && (
                    <div>
                      <div className="text-neutral-400">Last Order:</div>
                      <div className="text-white font-medium">{services['tradingActivity'].lastOrder.createdAtEt}</div>
                      <div className="text-neutral-500">({formatTime(services['tradingActivity'].lastOrder.createdAt)} - {services['tradingActivity'].lastOrder.symbol} {services['tradingActivity'].lastOrder.side})</div>
                    </div>
                  )}
                  {services['tradingActivity'].ordersLast24h !== undefined && (
                    <div className="text-neutral-400 mt-2">Orders (24h): <span className="text-white">{services['tradingActivity'].ordersLast24h}</span></div>
                  )}
                  {services['tradingActivity'].ordersLast3Days !== undefined && (
                    <div className="text-neutral-400">Orders (3d): <span className="text-white">{services['tradingActivity'].ordersLast3Days}</span></div>
                  )}
                </div>
              </div>
            )}

            {/* Rejection Analytics */}
            {services['rejectionAnalytics'] && (
              <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-2xl">{getStatusIcon(services['rejectionAnalytics'].status)}</span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    services['rejectionAnalytics'].status === 'healthy' ? 'bg-green-600 text-white' :
                    services['rejectionAnalytics'].status === 'warning' ? 'bg-yellow-600 text-white' :
                    'bg-red-600 text-white'
                  }`}>
                    {services['rejectionAnalytics'].status.toUpperCase()}
                  </span>
                </div>
                <h4 className="text-white font-semibold mb-1">Stock Screening Analytics</h4>
                <p className="text-sm text-neutral-300 mb-2">{services['rejectionAnalytics'].message}</p>
                <div className="text-xs space-y-1 text-neutral-400">
                  <div className="text-neutral-500 italic mb-2">
                    Tracks stocks rejected during screening (spread, liquidity, earnings blackout, etc.)
                  </div>
                  {services['rejectionAnalytics'].lastRecordedEt && (
                    <div>
                      <div className="text-neutral-400">Last Analytics:</div>
                      <div className="text-white font-medium">{services['rejectionAnalytics'].lastRecordedEt}</div>
                      <div className="text-neutral-500">({formatTime(services['rejectionAnalytics'].lastRecorded || '')})</div>
                    </div>
                  )}
                  {services['rejectionAnalytics'].recordsLast7Days !== undefined && (
                    <div className="text-neutral-400 mt-2">Screening Rejections (7d): <span className="text-white">{services['rejectionAnalytics'].recordsLast7Days}</span></div>
                  )}
                  {services['rejectionAnalytics'].recentTrades !== undefined && (
                    <div className="text-neutral-400">Recent Trades (7d): <span className="text-white">{services['rejectionAnalytics'].recentTrades}</span></div>
                  )}
                </div>
              </div>
            )}

          </div>
        </div>

        {/* Job Execution Status */}
        {services['jobExecution'] && (
          <div className="bg-neutral-900 rounded-lg border border-neutral-800 p-6">
            <h3 className="text-xl font-bold text-white mb-4">Job Execution Monitor</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-2xl">{getStatusIcon(services['jobExecution'].status)}</span>
                  <h4 className="text-white font-semibold">ET-Ticker</h4>
                  <span className="text-xs text-neutral-500">(Runs every 1 minute)</span>
                </div>
                {services['jobExecution'].lastEtTickerTriggerEt && (
                  <div className="space-y-1">
                    <div className="text-neutral-400 text-sm">Last Trigger:</div>
                    <div className="text-white font-medium">{services['jobExecution'].lastEtTickerTriggerEt}</div>
                    <div className="text-neutral-500 text-xs">({formatTime(services['jobExecution'].lastEtTickerTrigger || '')})</div>
                  </div>
                )}
                {services['jobExecution'].etTickerTriggersLastHour !== undefined && (
                  <div className="mt-2 text-sm">
                    <span className="text-neutral-400">Triggers (1h):</span>
                    <span className="text-white ml-2">{services['jobExecution'].etTickerTriggersLastHour}</span>
                    <span className="text-neutral-500 text-xs ml-2">(~60 expected)</span>
                  </div>
                )}
              </div>
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <h4 className="text-white font-semibold">Market-Screening</h4>
                  <span className="text-xs text-neutral-500">(Adaptive 10-20 min intervals)</span>
                </div>
                {services['jobExecution'].lastMarketScreeningEt && (
                  <div className="space-y-1">
                    <div className="text-neutral-400 text-sm">Last Execution:</div>
                    <div className="text-white font-medium">{services['jobExecution'].lastMarketScreeningEt}</div>
                    <div className="text-neutral-500 text-xs">({formatTime(services['jobExecution'].lastMarketScreening || '')})</div>
                  </div>
                )}
                {services['jobExecution'].marketScreeningLastHour !== undefined && (
                  <div className="mt-2 text-sm">
                    <span className="text-neutral-400">Executions (1h):</span>
                    <span className="text-white ml-2">{services['jobExecution'].marketScreeningLastHour}</span>
                    <span className="text-neutral-500 text-xs ml-2">(~3-6 expected during market hours)</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Phase 1: Recent Activity Log */}
        <div className="bg-neutral-900 rounded-lg border border-neutral-800 p-6">
          <h3 className="text-xl font-bold text-white mb-4">📊 Recent System Activity</h3>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {activities.map((activity) => (
              <div
                key={activity.id}
                className="flex items-start gap-3 p-3 rounded-lg hover:bg-neutral-800 transition-colors"
              >
                <span className="text-xl flex-shrink-0">{activity.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{activity.message}</div>
                  <div className="text-xs text-neutral-400">{formatTime(activity.timestamp)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Phase 2: Trading Statistics Dashboard */}
        {stats && (
          <div className="bg-neutral-900 rounded-lg border border-neutral-800 p-6">
            <h3 className="text-xl font-bold text-white mb-4">Today's Trading Statistics</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="bg-neutral-800 p-4 rounded-lg">
                <div className="text-sm text-neutral-400 mb-1">Total Orders</div>
                <div className="text-2xl font-bold text-white">{stats.totalOrders}</div>
                <div className="text-xs text-neutral-500">across all accounts</div>
              </div>
              <div className="bg-neutral-800 p-4 rounded-lg">
                <div className="text-sm text-neutral-400 mb-1">Fill Rate</div>
                <div className="text-2xl font-bold text-white">
                  {stats.totalOrders > 0 ? `${Math.round((stats.filledOrders / stats.totalOrders) * 100)}%` : '0%'}
                </div>
                <div className="text-xs text-neutral-500">
                  {stats.filledOrders} filled · {stats.rejectedOrders} rejected
                </div>
              </div>
              <div className="bg-neutral-800 p-4 rounded-lg">
                <div className="text-sm text-neutral-400 mb-1">Trades Executed</div>
                <div className="text-2xl font-bold text-white">{stats.trades}</div>
                <div className="text-xs text-neutral-500">since market open</div>
              </div>
              <div className="bg-neutral-800 p-4 rounded-lg">
                <div className="text-sm text-neutral-400 mb-1">Total Volume</div>
                <div className="text-2xl font-bold text-white">
                  ${stats.volume.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </div>
                <div className="text-xs text-neutral-500">approximate notional</div>
              </div>
              <div className="bg-neutral-800 p-4 rounded-lg">
                <div className="text-sm text-neutral-400 mb-1">Symbols Traded</div>
                <div className="text-2xl font-bold text-white">{stats.symbols}</div>
                <div className="text-xs text-neutral-500">unique tickers</div>
              </div>
              <div className="bg-neutral-800 p-4 rounded-lg">
                <div className="text-sm text-neutral-400 mb-1">Broker Rejections</div>
                <div className="text-2xl font-bold text-white">
                  {stats.rejectedOrders}
                </div>
                <div className="text-xs text-neutral-500">orders rejected by broker</div>
              </div>
            </div>

            {/* Show rejection details if any */}
            {stats.rejectedDetails && stats.rejectedDetails.length > 0 && (
              <div className="mt-4 bg-neutral-800 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-white mb-3">Rejection Details</h4>
                <div className="space-y-2">
                  {stats.rejectedDetails.map((rejection, idx) => (
                    <div key={idx} className="bg-neutral-900 p-3 rounded border border-neutral-700">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-white font-medium">{rejection.symbol}</span>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              rejection.side === 'BUY' ? 'bg-green-600/20 text-green-400' : 'bg-red-600/20 text-red-400'
                            }`}>
                              {rejection.side}
                            </span>
                          </div>
                          <div className="text-sm text-neutral-300 mb-1">
                            <span className="text-neutral-500">Reason:</span> {rejection.reason || 'No reason provided'}
                          </div>
                          {rejection.reasonCode && (
                            <div className="text-xs text-neutral-500">
                              Code: {rejection.reasonCode}
                            </div>
                          )}
                        </div>
                        <div className="text-xs text-neutral-500 text-right">
                          {new Date(rejection.createdAt).toLocaleTimeString('en-US', { 
                            hour: 'numeric', 
                            minute: '2-digit',
                            hour12: true 
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Phase 2: Configuration Status Panel */}
        <div className="bg-neutral-900 rounded-lg border border-neutral-800 p-6">
          <h3 className="text-xl font-bold text-white mb-4">Configuration Files</h3>
          <div className="space-y-3">
            {configs.map((config, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-4 bg-neutral-800 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">{getStatusIcon(config.status)}</span>
                  <div>
                    <div className="text-white font-medium">{config.name}</div>
                    {config.version && (
                      <div className="text-xs text-neutral-400">
                        Version {config.version}
                      </div>
                    )}
                    {config.message && (
                      <div className={`text-xs ${
                        config.status === 'loaded' ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {config.message}
                      </div>
                    )}
                  </div>
                </div>
                <span className={`px-3 py-1 rounded text-xs font-medium ${
                  config.status === 'loaded' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
                }`}>
                  {config.status.toUpperCase()}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Phase 2: Trading Day Summaries */}
        {(currentTradingDay || previousTradingDay) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Current Trading Day */}
            {currentTradingDay && (
              <div className="bg-neutral-900 rounded-lg border border-neutral-800 p-6">
                <h3 className="text-xl font-bold text-white mb-4">📅 Current Trading Day</h3>
                <div className="text-lg text-blue-400 mb-4">{currentTradingDay.date}</div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-neutral-300">Market-Screening Runs:</span>
                    <span className="text-white font-semibold">{currentTradingDay.marketScreeningRuns}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-neutral-300">Universe Runs:</span>
                    <span className="text-white font-semibold">{currentTradingDay.tradingUniverseRuns}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-neutral-300">Orders Placed:</span>
                    <span className="text-white font-semibold">{currentTradingDay.ordersPlaced}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Previous Trading Day */}
            {previousTradingDay && (
              <div className="bg-neutral-900 rounded-lg border border-neutral-800 p-6">
                <h3 className="text-xl font-bold text-white mb-4">📆 Previous Trading Day</h3>
                <div className="text-lg text-neutral-400 mb-4">{previousTradingDay.date}</div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-neutral-300">Market-Screening Runs:</span>
                    <span className="text-white font-semibold">{previousTradingDay.marketScreeningRuns}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-neutral-300">Universe Runs:</span>
                    <span className="text-white font-semibold">{previousTradingDay.tradingUniverseRuns}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-neutral-300">Orders Placed:</span>
                    <span className="text-white font-semibold">{previousTradingDay.ordersPlaced}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Phase 2: Scheduler Job Status */}
        <div className="bg-neutral-900 rounded-lg border border-neutral-800 p-6">
          <h3 className="text-xl font-bold text-white mb-4">Background Jobs (Last Hour)</h3>
          <div className="space-y-4">
            {jobs.filter(job => !['market-screening', 'automated-trader'].includes(job.name)).map((job, index) => (
              <div key={index} className="bg-neutral-800 rounded-lg p-4 border border-neutral-700">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-3">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{getStatusIcon(job.status)}</span>
                    <div>
                      <h4 className="text-white font-semibold capitalize">{job.name.replace(/-/g, ' ')}</h4>
                      <div className="text-xs text-neutral-400">
                        {job.executions} execution{job.executions !== '1' ? 's' : ''} in last hour
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded text-xs font-medium ${
                      job.status === 'healthy' ? 'bg-green-600 text-white' :
                      job.status === 'warning' ? 'bg-yellow-600 text-white' :
                      'bg-red-600 text-white'
                    }`}>
                      {job.status.toUpperCase()}
                    </span>
                    <span className="text-sm text-neutral-300">
                      {job.successRate} success
                    </span>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-neutral-400 mb-1">Last Run</div>
                    <div className="text-white">
                      {job.lastRun ? new Date(job.lastRun).toLocaleString('en-US', { timeZone: 'America/New_York' }) + ' ET' : 'Never'}
                    </div>
                    {job.lastRun && (
                      <div className="text-xs text-neutral-500 mt-1">{formatTime(job.lastRun)}</div>
                    )}
                  </div>
                  
                  {job.lastFinishedAt && (
                    <div>
                      <div className="text-neutral-400 mb-1">Last Finished</div>
                      <div className="text-green-400">
                        {new Date(job.lastFinishedAt).toLocaleString('en-US', { timeZone: 'America/New_York' }) + ' ET'}
                      </div>
                      <div className="text-xs text-neutral-500 mt-1">{formatTime(job.lastFinishedAt)}</div>
                    </div>
                  )}
                  
                  {job.lastFailedAt && (
                    <div>
                      <div className="text-neutral-400 mb-1">Last Failed</div>
                      <div className="text-red-400">
                        {new Date(job.lastFailedAt).toLocaleString('en-US', { timeZone: 'America/New_York' }) + ' ET'}
                      </div>
                      {job.failReason && (
                        <div className="text-xs text-red-300 mt-1 truncate" title={job.failReason}>
                          {job.failReason}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {job.nextRun && (
                    <div>
                      <div className="text-neutral-400 mb-1">Next Run</div>
                      <div className="text-cyan-400">
                        {new Date(job.nextRun).toLocaleString('en-US', { timeZone: 'America/New_York' }) + ' ET'}
                      </div>
                    </div>
                  )}
                  
                  {(job.failCount ?? 0) > 0 && (
                    <div>
                      <div className="text-neutral-400 mb-1">Fail Count</div>
                      <div className="text-red-400 font-semibold">{job.failCount}</div>
                    </div>
                  )}
                </div>
                
                {job.recentExecutions && job.recentExecutions.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-neutral-700">
                    <div className="text-sm font-medium text-neutral-400 mb-2">Recent Executions</div>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {job.recentExecutions.map((exec, execIndex) => (
                        <div key={execIndex} className="flex items-center justify-between text-xs bg-neutral-900/50 rounded p-2">
                          <div className="flex items-center gap-2">
                            <span className={exec.success ? 'text-green-400' : 'text-red-400'}>
                              {exec.success ? '✓' : '✗'}
                            </span>
                            <span className="text-neutral-300">
                              {exec.runAt ? new Date(exec.runAt).toLocaleString('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', second: '2-digit' }) + ' ET' : 'Unknown'}
                            </span>
                          </div>
                          {exec.error && (
                            <span className="text-red-400 truncate max-w-xs" title={exec.error}>
                              {exec.error}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Phase 3: Quick Actions Toolbar */}
        <div className="bg-neutral-900 rounded-lg border border-neutral-800 p-6">
          <h3 className="text-xl font-bold text-white mb-4">Quick Actions</h3>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handlePauseAll}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              ⏸️ Pause All Trading
            </button>
            <button
              onClick={handleResumeAll}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              ▶️ Resume All Trading
            </button>
            <button
              onClick={async () => {
                try {
                  await api.post('/system-health/actions', { action: 'force_reconciliation' });
                  alert('Portfolio reconciliation triggered successfully');
                  fetchData();
                } catch (error) {
                  console.error('Error triggering reconciliation:', error);
                  alert('Failed to trigger reconciliation');
                }
              }}
              className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              🔄 Force Reconciliation
            </button>
            <button
              className="px-4 py-2 bg-neutral-700 hover:bg-neutral-600 text-white rounded-lg text-sm font-medium transition-colors"
              onClick={fetchData}
            >
              📊 Refresh Data
            </button>
          </div>
        </div>

        {/* Phase 3: User Trading Activity Breakdown */}
        <div className="bg-neutral-900 rounded-lg border border-neutral-800 p-6">
          <h3 className="text-xl font-bold text-white mb-4">User Trading Activity</h3>
          <div className="space-y-4">
            {users.map((user) => (
              <div key={user.id} className="bg-neutral-800 p-4 rounded-lg">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-3">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{getStatusIcon(user.status)}</span>
                    <div>
                      <div className="text-white font-medium">{user.name}</div>
                      <div className="text-xs text-neutral-400">{user.email}</div>
                    </div>
                  </div>
                  <div className="flex gap-2 text-sm">
                    <span className="px-2 py-1 bg-neutral-700 rounded text-white">
                      {user.positions} positions
                    </span>
                    <span className={`px-2 py-1 rounded ${
                      parseFloat(user.pnl) >= 0 ? 'bg-green-600' : 'bg-red-600'
                    } text-white`}>
                      ${user.pnl} P&L
                    </span>
                  </div>
                </div>
                <div className="text-xs text-neutral-400 space-y-1">
                  <div>Last trade: {user.lastTradeMinutes !== null ? `${user.lastTradeMinutes}m ago` : 'No trades today'}</div>
                  <div>Orders today: {user.ordersToday} / 15</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Last Updated */}
        <div className="text-center text-xs text-neutral-500 pb-4">
          Last updated: {new Date().toLocaleTimeString()}
        </div>
      </div>
    </div>
  );
};

export default SystemHealth;

